// productMobileKit.jsx — briques d'UI partagées du module Produits mobile.
// Importé par : BrandsMobile, CategoriesMobile, UnitsMobile, ServicesMobile,
// AppointmentsMobile. Style : ./product-mobile-kit.css (namespace .epm).
//
// Réutilise l'helper d'icônes Lucide + slug du module Produits mobile.
import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Ico, fmt, genSlug } from "../produits/productsShared";
import MobileModuleHeader from "../MobileModuleHeader";
import "./product-mobile-kit.css";

export { Ico, fmt, genSlug };

// Récupère openMobileMenu fourni par le layout (comme MobileProductsPage).
// Retourne undefined hors d'un <Outlet> — sans planter.
export function useMobileMenu() {
  const ctx = useOutletContext();
  return ctx?.openMobileMenu;
}

/* ---------- helpers visuels ---------- */
export const hueOf = (s = "") => {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) % 360;
  return h;
};
export const monoStyle = (name) => {
  const h = hueOf(name || "?");
  return {
    background: `hsl(${h} 60% 94%)`,
    color: `hsl(${h} 45% 38%)`,
    boxShadow: `inset 0 0 0 1px hsl(${h} 40% 86%)`,
  };
};
export const firstLetter = (name = "") => (String(name).trim()[0] || "?").toUpperCase();
export const fmtMoney = (v) => `${fmt(v)} F`;

/* ---------- atomes ---------- */
export function Mono({ name, square }) {
  return <div className={"mono" + (square ? " mono--sq" : "")} style={monoStyle(name)}>{firstLetter(name)}</div>;
}
export function Pill({ tone = "off", label }) {
  if (!label) return null;
  return <span className={"pill " + tone}><span className="dot" />{label}</span>;
}
export function CountChip({ icon = "layers", value }) {
  if (value == null) return null;
  return <span className="countchip"><Ico name={icon} />{fmt(value)}</span>;
}

/* ---------- structure ---------- */
export function ProductScreen({ children }) {
  return <div className="epm"><div className="screen">{children}</div></div>;
}

export function Appbar({ title, subtitle = "", onMenu, action = null }) {
  return (
    <MobileModuleHeader
      title={title}
      subtitle={subtitle}
      onMenu={onMenu}
      actions={action}
    />
  );
}

export function Summary({ items }) {
  const valid = items.filter(Boolean);
  return (
    <div className="summary">
      {valid.map((it, i) => (
        <React.Fragment key={it.label}>
          {i > 0 ? <div className="summary__div" /> : null}
          <div className="summary__cell">
            <span className="summary__val">{it.value}</span>
            <span className="summary__lbl">{it.label}</span>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}

export function SearchBar({ value, onChange, placeholder = "Rechercher…" }) {
  return (
    <div className={"search" + (value ? " has-val" : "")}>
      <Ico name="search" />
      <input type="text" inputMode="search" placeholder={placeholder}
        value={value} onChange={(e) => onChange(e.target.value)} />
      <button className="search__clear" onClick={() => onChange("")}>
        <Ico name="x" style={{ width: 18, height: 18 }} />
      </button>
    </div>
  );
}

export function Chips({ value, onChange, options }) {
  return (
    <div className="chips">
      {options.map((o) => (
        <button key={o.key} className={"chip" + (value === o.key ? " on" : "")} onClick={() => onChange(o.key)}>
          {o.label}{o.n != null ? <span className="chip__n">{o.n}</span> : null}
        </button>
      ))}
    </div>
  );
}

export function EmptyState({ icon = "tag", title, text = "", loading = false }) {
  if (loading) {
    return (
      <div className="empty">
        <Ico name="loader-circle" className="spin" />
        <b>{title || "Chargement…"}</b>
        {text ? <span>{text}</span> : null}
      </div>
    );
  }
  return (
    <div className="empty">
      <div className="empty__art">
        <div className="empty__ring empty__ring--o" />
        <div className="empty__ring empty__ring--i" />
        <div className="empty__badge"><Ico name={icon} /></div>
      </div>
      <b>{title}</b>
      {text ? <span>{text}</span> : null}
    </div>
  );
}

export function Fab({ onClick, label = "Nouveau" }) {
  return <button className="fab" onClick={onClick}><Ico name="plus" />{label}</button>;
}

export function Sheet({ open, onClose, title, subtitle = "", children }) {
  if (!open) return null;
  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div className="sheet" role="dialog" aria-modal="true">
        <div className="sheet__grab" />
        {(title || subtitle) ? (
          <div className="sheet__head">
            <div>
              {title ? <h2>{title}</h2> : null}
              {subtitle ? <p>{subtitle}</p> : null}
            </div>
            <button className="iconbtn" onClick={onClose}><Ico name="x" /></button>
          </div>
        ) : null}
        {children}
      </div>
    </>
  );
}

export function ActionRow({ icon, label, danger = false, onClick, disabled = false }) {
  return (
    <button className={"actsheet__row" + (danger ? " danger" : "")} onClick={onClick} disabled={disabled}>
      <Ico name={icon} />{label}
    </button>
  );
}

/* ---------- champs ---------- */
export function Field({ label, hint = null, children }) {
  return (
    <label className="field">
      {label ? <span className="field__lbl">{label}{hint ? <small> {hint}</small> : null}</span> : null}
      {children}
    </label>
  );
}
export function TextInput({ value, onChange, placeholder = "", icon = null, autoFocus = false, readOnly = false, inputMode = "text", type = "text", autoComplete = undefined }) {
  return (
    <div className={"ctrl" + (readOnly ? " ctrl--ro" : "")}>
      <input type={type} value={value} placeholder={placeholder} autoFocus={autoFocus} readOnly={readOnly}
        inputMode={inputMode} autoComplete={autoComplete} onChange={(e) => onChange && onChange(e.target.value)} />
      {icon ? <Ico name={icon} style={{ width: 18, height: 18 }} /> : null}
    </div>
  );
}
export function TextAreaInput({ value, onChange, placeholder = "", rows = 3 }) {
  return (
    <div className="ctrl ctrl--area">
      <textarea rows={rows} value={value} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
export function SelectInput({ value, onChange, options, placeholder = "Choisir…" }) {
  return (
    <div className="ctrl ctrl--select">
      <select value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)}>
        <option value="">{placeholder}</option>
        {options.map((o) => <option key={String(o.value)} value={o.value}>{o.label}</option>)}
      </select>
      <Ico name="chevron-down" />
    </div>
  );
}
export function Segmented({ value, onChange, options }) {
  return (
    <div className="seg">
      {options.map((o) => (
        <button key={String(o.value)} className={value === o.value ? "on" : ""} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}
export function SwitchRow({ label, sub = null, value, onChange, disabled = false }) {
  return (
    <div className="toggle-row">
      <div>
        <div className="field__lbl" style={{ marginBottom: 2 }}>{label}</div>
        {sub ? <div className="toggle-sub">{sub}</div> : null}
      </div>
      <button className={"switch" + (value ? " on" : "")} onClick={() => onChange(!value)} disabled={disabled}><span /></button>
    </div>
  );
}
export function DetailRow({ label, value }) {
  return <div className="drow"><span className="drow__lbl">{label}</span><span className="drow__val">{value}</span></div>;
}
export function Note({ children }) {
  return <div className="note"><Ico name="alert-circle" />{children}</div>;
}

/* ---------- hook de liste ---------- */
// load: () => Promise<rows[]>. deps : tableau de dépendances (entreprise…).
export function useResourceList(load, deps = []) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const reload = useMemo(() => async () => {
    setLoading(true);
    try {
      const data = await load();
      setRows(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => { reload(); }, [reload]);

  return { rows, setRows, loading, reload };
}
